import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const PASSWORD_MIN_LENGTH = 8;

const EXPIRED_RESET_CODES = new Set([
  "bad_jwt",
  "otp_expired",
  "refresh_token_already_used",
  "refresh_token_not_found",
  "session_expired",
  "session_not_found",
  "flow_state_expired",
  "flow_state_not_found"
]);

const TRANSIENT_AUTH_CODES = new Set([
  "request_timeout",
  "unexpected_failure",
  "conflict",
  "hook_timeout",
  "hook_timeout_after_retry"
]);

function getNormalizedAuthDetails(error) {
  const reasons = Array.isArray(error?.reasons) ? error.reasons : [];
  const reasonText = reasons
    .map((reason) =>
      typeof reason === "string"
        ? reason
        : JSON.stringify(reason)
    )
    .join(" ")
    .toLowerCase();

  return [
    error?.message,
    error?.error_description,
    error?.details,
    reasonText
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function getPasswordUpdateErrorMessage(error) {
  const code = error?.code;
  const details = getNormalizedAuthDetails(error);

  if (code === "weak_password" || details.includes("weak password")) {
    const appearsLeaked =
      details.includes("pwned") ||
      details.includes("breach") ||
      details.includes("leaked") ||
      details.includes("compromised");

    if (appearsLeaked) {
      return "This password can't be used because it appears in a database of passwords exposed in known data breaches. For your security, please choose a different, unique password or passphrase that you do not use on another site.";
    }

    const missingCharacters =
      details.includes("character") ||
      details.includes("uppercase") ||
      details.includes("lowercase") ||
      details.includes("digit") ||
      details.includes("number") ||
      details.includes("symbol");

    if (missingCharacters) {
      return "This password doesn't meet Toren's security requirements. Please use a stronger password with the required mix of uppercase and lowercase letters, numbers, and symbols.";
    }

    const tooShort =
      details.includes("length") ||
      details.includes("too short") ||
      details.includes("characters long");

    if (tooShort) {
      return `This password is too short. Please use at least ${PASSWORD_MIN_LENGTH} characters; twelve or more is recommended.`;
    }

    return "This password doesn't meet Toren's security requirements. Please choose a longer, unique password or passphrase with a mix of letters, numbers, and symbols, and do not reuse a password from another site.";
  }

  if (code === "same_password") {
    return "Your new password must be different from your current password. Please choose a new password you have not used for this Toren account.";
  }

  if (EXPIRED_RESET_CODES.has(code)) {
    return "This password reset link has expired, has already been used, or is no longer valid. Please request a new password reset email and use the newest link.";
  }

  if (
    code === "reauthentication_needed" ||
    code === "reauthentication_not_valid"
  ) {
    return "For your security, this password change needs a fresh verification. Please request a new password reset email and use the newest link.";
  }

  if (
    code === "over_request_rate_limit" ||
    code === "over_email_send_rate_limit"
  ) {
    return "Too many password-reset attempts were made in a short period. Please wait a few minutes before trying again.";
  }

  if (code === "user_not_found") {
    return "We couldn't find an active Toren account for this reset session. Please return to sign in and request a new password reset email.";
  }

  if (TRANSIENT_AUTH_CODES.has(code)) {
    return "Toren couldn't complete the password update right now. Your password was not changed. Please wait a moment and try again.";
  }

  if (code === "validation_failed") {
    return "The password update request could not be completed. Please check the new password and try again.";
  }

  return "We couldn't update your password. Your password was not changed. Please try again, or request a new password reset email if the problem continues.";
}

export function getPasswordResetEmailErrorMessage(error) {
  const code = error?.code;

  if (
    code === "over_request_rate_limit" ||
    code === "over_email_send_rate_limit"
  ) {
    return "A password reset was requested too many times in a short period. Please wait a few minutes before requesting another email.";
  }

  if (code === "email_address_invalid") {
    return "That email address can't be used for password recovery. Please check the address and try again.";
  }

  if (code === "email_address_not_authorized") {
    return "Toren couldn't send a password reset email to that address. Please contact your Toren administrator for assistance.";
  }

  if (code === "email_provider_disabled" || code === "otp_disabled") {
    return "Password recovery is temporarily unavailable. Please contact your Toren administrator.";
  }

  if (TRANSIENT_AUTH_CODES.has(code)) {
    return "Toren couldn't send the password reset email right now. Please wait a moment and try again.";
  }

  return "We couldn't send the password reset email. Please confirm the email address and try again.";
}

export default function Login({ initialMode = "login", onPasswordUpdated, onLoginSuccess }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("");
  const [statusType, setStatusType] = useState("info");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const isPasswordRecovery = mode === "passwordRecovery";
  const passwordMeetsMinimum =
    newPassword.length >= PASSWORD_MIN_LENGTH;
  const passwordsMatch =
    confirmPassword.length > 0 && newPassword === confirmPassword;
  const canUpdatePassword =
    passwordMeetsMinimum && passwordsMatch && !loading;

  async function handleLogin(e) {
    e.preventDefault();

    setLoading(true);
    setStatus("");
    setStatusType("info");

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      setStatus("We couldn't sign you in. Please check your email and password.");
      setStatusType("error");
    } else if (typeof onLoginSuccess === "function") {
      onLoginSuccess();
    }

    setLoading(false);
  }

  async function handleForgotPassword() {
    if (!email) {
      setStatus("Enter your email address first, then click forgot password.");
      setStatusType("error");
      return;
    }

    setResetLoading(true);
    setStatus("");
    setStatusType("info");

    const baseUrl =
      window.location.hostname === "localhost"
        ? "http://localhost:3000"
        : window.location.origin;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${baseUrl}/reset-password`
    });

    if (error) {
      console.error("Password reset email failed:", {
        name: error.name,
        code: error.code,
        status: error.status,
        message: error.message
      });

      setStatus(getPasswordResetEmailErrorMessage(error));
      setStatusType("error");
    } else {
      setStatus("Password reset email sent. Check your inbox.");
      setStatusType("success");
    }

    setResetLoading(false);
  }

  async function handleUpdatePassword(e) {
    e.preventDefault();

    setLoading(true);
    setStatus("");
    setStatusType("info");

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setStatus(
        `Your new password must be at least ${PASSWORD_MIN_LENGTH} characters.`
      );
      setStatusType("error");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus("The new passwords do not match.");
      setStatusType("error");
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error("Password update failed:", {
        name: error.name,
        code: error.code,
        status: error.status,
        message: error.message
      });

      setStatus(getPasswordUpdateErrorMessage(error));
      setStatusType("error");
      setLoading(false);
      return;
    }

    setStatus("Password updated. Please sign in with your new password.");
    setStatusType("success");
    setNewPassword("");
    setConfirmPassword("");
    setPassword("");

    await supabase.auth.signOut();

    if (typeof onPasswordUpdated === "function") {
      onPasswordUpdated();
    }

    setMode("login");
    setLoading(false);
  }

  const labelStyle = {
    display: "block",
    marginBottom: 6,
    color: "#374151",
    fontSize: 13,
    fontWeight: 700
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: "11px 12px",
    marginBottom: 14,
    fontSize: 14,
    outline: "none"
  };

  const primaryButtonStyle = {
    width: "100%",
    border: "none",
    borderRadius: 999,
    background: loading ? "#9ca3af" : "#1f2937",
    color: "#ffffff",
    padding: "11px 16px",
    fontSize: 14,
    fontWeight: 700,
    cursor: loading ? "not-allowed" : "pointer"
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 18,
          boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
          padding: "34px 32px"
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <img
            src="/toren-logo-navy-tm.svg"
            alt="Toren"
            style={{
              width: 160,
              height: "auto",
              display: "block",
              marginBottom: 24
            }}
          />

          <h1
            style={{
              margin: 0,
              color: "#1f2937",
              fontSize: 24,
              lineHeight: 1.2,
              fontWeight: 700
            }}
          >
            {isPasswordRecovery ? "Set a new password" : "Sign in to Toren"}
          </h1>

          <p
            style={{
              margin: "8px 0 0",
              color: "#6b7280",
              fontSize: 14,
              lineHeight: 1.5
            }}
          >
            {isPasswordRecovery
              ? "Enter a new password for your Toren account."
              : "Access your AI Intelligence Platform dashboard."}
          </p>
        </div>

        {isPasswordRecovery ? (
          <form onSubmit={handleUpdatePassword}>
            <label style={labelStyle}>New password</label>

            <input
              type="password"
              value={newPassword}
              autoComplete="new-password"
              aria-describedby="password-guidance"
              aria-invalid={
                newPassword.length > 0 && !passwordMeetsMinimum
              }
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
              spellCheck={false}
              style={inputStyle}
            />

            <div
              id="password-guidance"
              style={{
                margin: "-2px 0 18px",
                padding: "12px 14px",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                background: "#f9fafb",
                color: "#4b5563",
                fontSize: 12,
                lineHeight: 1.5
              }}
            >
              <div
                style={{
                  color: "#374151",
                  fontWeight: 700,
                  marginBottom: 6
                }}
              >
                Create a strong password
              </div>

              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18
                }}
              >
                <li
                  style={{
                    color: passwordMeetsMinimum
                      ? "#047857"
                      : "#4b5563",
                    marginBottom: 4
                  }}
                >
                  {passwordMeetsMinimum ? "✓ " : ""}
                  At least {PASSWORD_MIN_LENGTH} characters. Twelve or
                  more is recommended.
                </li>
                <li style={{ marginBottom: 4 }}>
                  Use a unique password or passphrase that you do not
                  use anywhere else.
                </li>
                <li style={{ marginBottom: 4 }}>
                  Mix uppercase and lowercase letters, numbers, and
                  symbols.
                </li>
                <li style={{ marginBottom: 4 }}>
                  Avoid names, company terms, dates, and common
                  phrases.
                </li>
                <li style={{ marginBottom: 4 }}>
                  Passwords found in known data breaches cannot be
                  used.
                </li>
                <li>
                  A password manager can generate and securely save
                  your password.
                </li>
              </ul>
            </div>

            <label style={labelStyle}>Confirm new password</label>

            <input
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              aria-describedby="password-match-status"
              aria-invalid={
                confirmPassword.length > 0 && !passwordsMatch
              }
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
              spellCheck={false}
              style={{
                ...inputStyle,
                marginBottom: 8
              }}
            />

            <div
              id="password-match-status"
              aria-live="polite"
              style={{
                minHeight: 18,
                marginBottom: 14,
                color:
                  confirmPassword.length === 0
                    ? "#6b7280"
                    : passwordsMatch
                    ? "#047857"
                    : "#b91c1c",
                fontSize: 12,
                lineHeight: 1.4
              }}
            >
              {confirmPassword.length === 0
                ? "Re-enter your password to confirm it."
                : passwordsMatch
                ? "✓ Passwords match."
                : "The passwords do not match."}
            </div>

            <button
              type="submit"
              disabled={!canUpdatePassword}
              style={{
                ...primaryButtonStyle,
                background: canUpdatePassword
                  ? "#1f2937"
                  : "#9ca3af",
                cursor: canUpdatePassword
                  ? "pointer"
                  : "not-allowed"
              }}
            >
              {loading ? "Updating password..." : "Update password"}
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={handleLogin}>
              <label style={labelStyle}>Email</label>

              <input
                type="email"
                value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
              />

              <label style={labelStyle}>Password</label>

              <input
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  ...inputStyle,
                  marginBottom: 16
                }}
              />

              <button
                type="submit"
                disabled={loading}
                style={primaryButtonStyle}
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading}
              style={{
                marginTop: 14,
                width: "100%",
                border: "none",
                background: "transparent",
                color: "#4b5563",
                fontSize: 13,
                fontWeight: 600,
                cursor: resetLoading ? "not-allowed" : "pointer"
              }}
            >
              {resetLoading ? "Sending reset email..." : "Forgot password?"}
            </button>
          </>
        )}

        {status && (
          <div
            role={statusType === "error" ? "alert" : "status"}
            aria-live="polite"
            style={{
              marginTop: 16,
              padding: "10px 12px",
              borderRadius: 10,
              fontSize: 13,
              lineHeight: 1.4,
              background:
                statusType === "success"
                  ? "#ecfdf5"
                  : statusType === "error"
                  ? "#fef2f2"
                  : "#f9fafb",
              color:
                statusType === "success"
                  ? "#047857"
                  : statusType === "error"
                  ? "#b91c1c"
                  : "#374151",
              border:
                statusType === "success"
                  ? "1px solid #a7f3d0"
                  : statusType === "error"
                  ? "1px solid #fecaca"
                  : "1px solid #e5e7eb"
            }}
          >
            {status}
          </div>
        )}

        <div
          style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: "1px solid #e5e7eb",
            color: "#6b7280",
            fontSize: 13,
            lineHeight: 1.5,
            textAlign: "center"
          }}
        >
          Looking for Toren?{" "}
          <a
            href="https://torenindex.com"
            style={{
              color: "#1f2937",
              fontWeight: 700,
              textDecoration: "none"
            }}
          >
            Visit torenindex.com
          </a>
        </div>
      </div>
    </div>
  );
}
